/**
 * Shared date/time formatting helpers for the worker portal.
 *
 * WHY THIS EXISTS (timezone bug, 2026-06-26)
 * ------------------------------------------
 * Several worker-portal surfaces extracted the wall-clock time straight
 * out of an ISO datetime string with a regex:
 *
 *     clock_in.match(/T(\d{2}:\d{2})/)?.[1]
 *
 * That returns the UTC wall clock verbatim (the backend serializes
 * clock_in/clock_out as UTC ISO), so a worker in UTC+8 saw "01:31"
 * on the Clock page chips while the punch stream (which used
 * `new Date(...).toLocaleTimeString()`) correctly showed "09:31" —
 * an 8-hour mismatch on the SAME page. See WorkerClock / WorkerDashboard
 * / WorkerTeam.
 *
 * `fmtLocalTime` is the single source of truth: it converts an ISO
 * datetime to the browser's local time, and passes through values that
 * are already an org-local "HH:MM" (the /me/time-entries/daily endpoint
 * returns those).
 *
 * WHY `activeLocale` EXISTS (locale bug, 2026-07-28)
 * -------------------------------------------------
 * `Intl` and every `toLocale*String()` call default to the **browser's**
 * language, which is not the language the user picked in this app. With the UI
 * in 简体中文 the worker dashboard's month card still read "July 2026" and an
 * announcement date read "6/11/2026" — the numbers and month names came from
 * Chrome's locale, not from i18next. Sixty-plus call sites had this shape while
 * thirteen already passed `i18n.language`; `activeLocale()` is that one
 * convention, reading the live i18next language so a language switch reformats
 * dates and numbers along with the copy.
 */
import i18next from 'i18next';

/** The language the UI is currently rendered in — never the browser's. */
export function activeLocale(): string {
  // Read the i18next SINGLETON, not `src/i18n` — importing the app's init
  // module from a util that half the pages import would drag i18next.init()
  // (and every locale bundle) into every one of them, and it broke four test
  // suites that mock react-i18next: `initReactI18next` came back undefined and
  // init threw at import time.
  //
  // The `as any` matches src/i18n/index.ts: this i18next version ships types
  // whose default export does not expose `language`, and the app already casts
  // where it reads it. One convention, not two.
  return (i18next as any).language || 'en';
}

/** Format an ISO datetime (UTC) — or an already-local "HH:MM" — to the
 *  browser's local "HH:MM". Returns the `empty` placeholder for null. */
export function fmtLocalTime(
  value: string | null | undefined,
  empty = '—',
): string {
  if (!value) return empty;
  // Backend /me/time-entries/daily returns org-local "HH:MM" already —
  // pass through without re-parsing (a bare "HH:MM" isn't a valid Date).
  if (/^\d{1,2}:\d{2}$/.test(value)) return value;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) {
    // Legacy / unexpected shape — fall back to raw extraction so we
    // never render "Invalid Date".
    const m = value.match(/T(\d{2}:\d{2})/);
    return m ? m[1] : empty;
  }
  return d.toLocaleTimeString(activeLocale(), { hour: '2-digit', minute: '2-digit' });
}

/** Format an ISO datetime to a locale-aware date AND time.
 *
 *  For "who did this, when" lines (the import history, §11.4) where the date
 *  matters as much as the time. Uses `activeLocale()` for the same reason every
 *  other helper here does: a bare `toLocaleString()` silently formats in the
 *  BROWSER's language, so a zh-CN admin on an en-US laptop reads one line of
 *  English inside an otherwise Chinese page. */
export function fmtLocalDateTime(
  value: string | null | undefined,
  empty = '—',
): string {
  if (!value) return empty;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return empty;
  return d.toLocaleString(activeLocale(), {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Format an ISO datetime to local "HH:MM:SS" (for the clock-records list). */
export function fmtLocalTimeWithSeconds(
  value: string | null | undefined,
  empty = '—',
): string {
  if (!value) return empty;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return empty;
  return d.toLocaleTimeString(activeLocale(), {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

/**
 * A CALENDAR date (`YYYY-MM-DD`, no time, no zone) as the reader's locale writes it.
 *
 * Attendance requests store their days as bare dates. They reached the approval rows as the raw
 * `2026-06-20`. Parsing that with `new Date()` would also be wrong: it means UTC midnight, which is
 * the PREVIOUS day west of Greenwich. So the date is formatted IN UTC, and it stays the day the
 * worker picked. The year is shown only when it is not this year. Anything that is not a bare date
 * passes through unchanged.
 * Mobile twin: svdy-mobile/src/i18n/format.ts `formatCalendarDate`.
 */
export function fmtCalendarDate(value: string | null | undefined, locale = activeLocale()): string {
  if (!value) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.slice(0, 10));
  if (!m || value.length > 10) return value;
  const d = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3]));
  if (Number.isNaN(d.getTime())) return value;
  const sameYear = +m[1] === new Date().getFullYear();
  try {
    return d.toLocaleDateString(locale, {
      timeZone: 'UTC', month: 'short', day: 'numeric', ...(sameYear ? {} : { year: 'numeric' }),
    });
  } catch {
    return value;
  }
}

/** `start → end` of calendar dates; one date when they are the same day. */
export function fmtCalendarRange(
  start: string | null | undefined,
  end: string | null | undefined,
  locale = activeLocale(),
): string {
  const a = fmtCalendarDate(start, locale);
  const b = fmtCalendarDate(end, locale);
  if (a && b) return start === end ? a : `${a} → ${b}`;
  return a || b;
}

export type DurationUnit = 'minute' | 'hour' | 'day';

/**
 * A number with a time unit, in the reader's locale: `7.5 hr` / `7,5 Std.` / `7.5小时` / `3 日`.
 *
 * Template literals like `${hours}h` and `${days}d` put English abbreviations into every locale.
 * CLDR `short`, not `narrow`: the narrow Japanese form became Latin `7.5h` / `3d` in recent ICU
 * (Node 26 / ICU 78), so it is English again in exactly the locale it was meant to fix.
 */
export function fmtUnit(value: number, unit: DurationUnit, locale = activeLocale(), maxFraction = 1): string {
  try {
    return new Intl.NumberFormat(locale, {
      style: 'unit', unit, unitDisplay: 'short', maximumFractionDigits: maxFraction,
    }).format(value);
  } catch {
    return `${value} ${unit}`;
  }
}

/** Whole minutes as `7 hr 30 min` in the reader's locale (`7小时 30分钟`). */
export function fmtMinutesSpan(minutes: number, locale = activeLocale()): string {
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  if (h === 0) return fmtUnit(m, 'minute', locale, 0);
  if (m === 0) return fmtUnit(h, 'hour', locale, 0);
  return `${fmtUnit(h, 'hour', locale, 0)} ${fmtUnit(m, 'minute', locale, 0)}`;
}

/** Elapsed time since `iso`, coarse: `<1 hr`, `5 hr`, `3 days` — localized units. */
export function fmtAge(iso: string | null | undefined, locale = activeLocale()): string {
  if (!iso) return '—';
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return '—';
  const hours = (Date.now() - t) / 3_600_000;
  if (hours < 1) return `<${fmtUnit(1, 'hour', locale)}`;
  if (hours < 24) return fmtUnit(Math.floor(hours), 'hour', locale);
  return fmtUnit(Math.floor(hours / 24), 'day', locale);
}
